/**
 * Report Export Utility with Infuse Branding
 * Exports wellness reports to PDF with professional branding
 */

import jsPDF from 'jspdf';

// Infuse Brand Colors (matching the logo gradient)
const BRAND_COLORS = {
  primary: '#F5A623',    // Golden/Orange from logo
  secondary: '#FF8C00',  // Darker orange
  accent: '#FFD700',     // Gold
  dark: '#1a1a2e',       // Dark background
  text: '#333333',
  lightText: '#666666',
};

// Infuse Contact Info
const INFUSE_CONTACT = {
  website: 'www.infuse.net.in',
  email: 'info@infuse.net.in',
  tagline: 'Empowering Health Through Intelligence',
};

/**
 * Generate PDF report with Infuse branding
 */
export const exportToPDF = async (plan, patientInfo = {}) => {
  const doc = new jsPDF('p', 'mm', 'a4');
  const pageWidth = doc.internal.pageSize.getWidth();
  const pageHeight = doc.internal.pageSize.getHeight();
  const margin = 20;
  let yPos = margin;

  // Helper functions
  const addHeader = () => {
    // Header background
    doc.setFillColor(26, 26, 46); // Dark background
    doc.rect(0, 0, pageWidth, 40, 'F');
    
    // Brand name
    doc.setTextColor(245, 166, 35); // Brand orange
    doc.setFontSize(24);
    doc.setFont('helvetica', 'bold');
    doc.text('INFUSE', margin, 18);
    
    // Tagline
    doc.setTextColor(255, 255, 255);
    doc.setFontSize(10);
    doc.setFont('helvetica', 'normal');
    doc.text(INFUSE_CONTACT.tagline, margin, 26);
    
    // Contact info on right
    doc.setFontSize(8);
    doc.setTextColor(200, 200, 200);
    doc.text(INFUSE_CONTACT.website, pageWidth - margin, 18, { align: 'right' });
    doc.text(INFUSE_CONTACT.email, pageWidth - margin, 24, { align: 'right' });
    
    return 50; // Return next y position
  };

  const addFooter = (pageNum, totalPages) => {
    const footerY = pageHeight - 15;
    
    // Footer line
    doc.setDrawColor(245, 166, 35);
    doc.setLineWidth(0.5);
    doc.line(margin, footerY - 5, pageWidth - margin, footerY - 5);
    
    // Footer text
    doc.setFontSize(8);
    doc.setTextColor(100, 100, 100);
    doc.text(`Page ${pageNum} of ${totalPages}`, pageWidth / 2, footerY, { align: 'center' });
    doc.text('Confidential - Generated by Infuse AI', margin, footerY);
    doc.text(new Date().toLocaleDateString(), pageWidth - margin, footerY, { align: 'right' });
  };

  const checkNewPage = (neededSpace) => {
    if (yPos + neededSpace > pageHeight - 30) {
      doc.addPage();
      yPos = addHeader();
      return true;
    }
    return false;
  };

  const addSectionTitle = (title, icon = '') => {
    checkNewPage(20);
    doc.setFillColor(245, 166, 35);
    doc.rect(margin - 2, yPos - 5, 4, 12, 'F');
    doc.setTextColor(26, 26, 46);
    doc.setFontSize(14);
    doc.setFont('helvetica', 'bold');
    doc.text(`${icon} ${title}`, margin + 5, yPos + 2);
    yPos += 12;
  };

  const addText = (text, fontSize = 10, color = BRAND_COLORS.text) => {
    checkNewPage(10);
    doc.setFontSize(fontSize);
    doc.setTextColor(color);
    doc.setFont('helvetica', 'normal');
    
    const lines = doc.splitTextToSize(text, pageWidth - (margin * 2));
    doc.text(lines, margin, yPos);
    yPos += lines.length * (fontSize * 0.5) + 3;
  };

  const addBulletPoint = (text) => {
    checkNewPage(8);
    doc.setFontSize(10);
    doc.setTextColor(51, 51, 51);
    doc.setFont('helvetica', 'normal');
    
    // Bullet
    doc.setFillColor(245, 166, 35);
    doc.circle(margin + 2, yPos - 1.5, 1.5, 'F');
    
    const lines = doc.splitTextToSize(text, pageWidth - (margin * 2) - 10);
    doc.text(lines, margin + 8, yPos);
    yPos += lines.length * 5 + 2;
  };

  // ============== BUILD THE REPORT ==============

  // Page 1: Cover
  yPos = addHeader();
  
  // Report Title
  yPos += 20;
  doc.setTextColor(26, 26, 46);
  doc.setFontSize(28);
  doc.setFont('helvetica', 'bold');
  doc.text('AI Lifestyle', pageWidth / 2, yPos, { align: 'center' });
  yPos += 12;
  doc.text('Correction Plan', pageWidth / 2, yPos, { align: 'center' });
  
  // Decorative line
  yPos += 10;
  doc.setDrawColor(245, 166, 35);
  doc.setLineWidth(2);
  doc.line(pageWidth / 2 - 30, yPos, pageWidth / 2 + 30, yPos);
  
  // Patient Info Box
  yPos += 20;
  doc.setFillColor(250, 250, 250);
  doc.roundedRect(margin, yPos, pageWidth - (margin * 2), 40, 5, 5, 'F');
  
  doc.setFontSize(12);
  doc.setTextColor(100, 100, 100);
  doc.text('Prepared For:', margin + 10, yPos + 12);
  doc.setTextColor(26, 26, 46);
  doc.setFont('helvetica', 'bold');
  doc.text(patientInfo.name || 'Patient', margin + 10, yPos + 22);
  
  doc.setFont('helvetica', 'normal');
  doc.setTextColor(100, 100, 100);
  doc.text('Generated:', pageWidth - margin - 60, yPos + 12);
  doc.setTextColor(26, 26, 46);
  doc.text(new Date().toLocaleDateString('en-IN', { 
    day: 'numeric', 
    month: 'long', 
    year: 'numeric' 
  }), pageWidth - margin - 60, yPos + 22);

  // Focus Areas
  yPos += 55;
  doc.setFontSize(12);
  doc.setTextColor(100, 100, 100);
  doc.text('Focus Areas:', margin, yPos);
  yPos += 8;
  
  if (plan.focus_areas && plan.focus_areas.length > 0) {
    plan.focus_areas.forEach((area, idx) => {
      doc.setFillColor(245, 166, 35, 0.2);
      const areaText = area.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
      const textWidth = doc.getTextWidth(areaText) + 10;
      doc.roundedRect(margin + (idx * 45), yPos, textWidth, 8, 2, 2, 'F');
      doc.setFontSize(9);
      doc.setTextColor(200, 100, 0);
      doc.text(areaText, margin + (idx * 45) + 5, yPos + 5.5);
    });
  }

  // Quick Stats
  yPos += 25;
  const statsBoxWidth = (pageWidth - (margin * 2) - 15) / 4;
  const stats = [
    { label: 'Daily Calories', value: plan.diet_plan?.daily_calorie_target || 'N/A', unit: 'kcal' },
    { label: 'Hydration', value: plan.diet_plan?.hydration_target_liters || 'N/A', unit: 'L' },
    { label: 'Weekly Exercise', value: plan.workout_plan?.total_weekly_minutes || 'N/A', unit: 'min' },
    { label: 'Daily Yoga', value: plan.yoga_plan?.total_duration_minutes || 'N/A', unit: 'min' },
  ];

  stats.forEach((stat, idx) => {
    const x = margin + (idx * (statsBoxWidth + 5));
    doc.setFillColor(26, 26, 46);
    doc.roundedRect(x, yPos, statsBoxWidth, 25, 3, 3, 'F');
    
    doc.setTextColor(150, 150, 150);
    doc.setFontSize(8);
    doc.text(stat.label, x + statsBoxWidth / 2, yPos + 8, { align: 'center' });
    
    doc.setTextColor(245, 166, 35);
    doc.setFontSize(14);
    doc.setFont('helvetica', 'bold');
    doc.text(`${stat.value}`, x + statsBoxWidth / 2, yPos + 18, { align: 'center' });
    
    doc.setTextColor(200, 200, 200);
    doc.setFontSize(8);
    doc.setFont('helvetica', 'normal');
    doc.text(stat.unit, x + statsBoxWidth / 2 + doc.getTextWidth(`${stat.value}`) / 2 + 3, yPos + 18);
  });

  addFooter(1, 4);

  // Page 2: Diet Plan
  doc.addPage();
  yPos = addHeader();
  
  addSectionTitle('Diet Plan', '🥗');
  yPos += 5;

  // Macro Distribution
  if (plan.diet_plan?.macro_distribution) {
    doc.setFontSize(11);
    doc.setTextColor(100, 100, 100);
    doc.text('Macro Distribution:', margin, yPos);
    yPos += 8;
    
    const macros = plan.diet_plan.macro_distribution;
    doc.setFontSize(10);
    doc.setTextColor(51, 51, 51);
    doc.text(`Carbohydrates: ${macros.carbs}% | Protein: ${macros.protein}% | Fats: ${macros.fats}%`, margin, yPos);
    yPos += 10;
  }

  // Meals
  if (plan.diet_plan?.meals) {
    doc.setFontSize(11);
    doc.setTextColor(100, 100, 100);
    doc.text('Daily Meal Plan:', margin, yPos);
    yPos += 8;
    
    plan.diet_plan.meals.forEach(meal => {
      checkNewPage(15);
      doc.setFontSize(10);
      doc.setFont('helvetica', 'bold');
      doc.setTextColor(26, 26, 46);
      const mealName = meal.meal_type.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
      doc.text(`${mealName} (${meal.time}) - ${meal.calories} kcal`, margin, yPos);
      yPos += 5;
      
      doc.setFont('helvetica', 'normal');
      doc.setTextColor(100, 100, 100);
      doc.text(meal.foods.join(', '), margin + 5, yPos);
      yPos += 7;
    });
  }

  // Foods to Include/Avoid
  yPos += 5;
  if (plan.diet_plan?.foods_to_include) {
    doc.setFontSize(11);
    doc.setTextColor(34, 139, 34); // Green
    doc.text('Foods to Include:', margin, yPos);
    yPos += 6;
    plan.diet_plan.foods_to_include.slice(0, 5).forEach(food => {
      addBulletPoint(food);
    });
  }

  yPos += 3;
  if (plan.diet_plan?.foods_to_avoid) {
    doc.setFontSize(11);
    doc.setTextColor(220, 20, 60); // Red
    doc.text('Foods to Avoid:', margin, yPos);
    yPos += 6;
    plan.diet_plan.foods_to_avoid.slice(0, 5).forEach(food => {
      addBulletPoint(food);
    });
  }

  addFooter(2, 4);

  // Page 3: Workout Plan
  doc.addPage();
  yPos = addHeader();
  
  addSectionTitle('Workout Plan', '🏋️');
  yPos += 5;

  if (plan.workout_plan?.exercises) {
    plan.workout_plan.exercises.forEach(exercise => {
      checkNewPage(20);
      
      doc.setFillColor(240, 240, 240);
      doc.roundedRect(margin, yPos - 3, pageWidth - (margin * 2), 18, 2, 2, 'F');
      
      doc.setFontSize(11);
      doc.setFont('helvetica', 'bold');
      doc.setTextColor(26, 26, 46);
      doc.text(exercise.name, margin + 5, yPos + 3);
      
      doc.setFont('helvetica', 'normal');
      doc.setFontSize(9);
      doc.setTextColor(100, 100, 100);
      doc.text(`${exercise.duration_minutes} min | ${exercise.intensity} intensity | ${exercise.calories_burned || 0} kcal`, margin + 5, yPos + 10);
      
      yPos += 22;
    });
  }

  // Weekly Schedule
  yPos += 5;
  doc.setFontSize(11);
  doc.setTextColor(100, 100, 100);
  doc.text('Rest Days: ' + (plan.workout_plan?.rest_days?.join(', ') || 'Thursday, Sunday'), margin, yPos);
  yPos += 8;

  if (plan.workout_plan?.progression_notes) {
    doc.setFontSize(10);
    doc.setFont('helvetica', 'italic');
    doc.setTextColor(100, 100, 100);
    const notes = doc.splitTextToSize(plan.workout_plan.progression_notes, pageWidth - (margin * 2));
    doc.text(notes, margin, yPos);
  }

  addFooter(3, 4);

  // Page 4: Yoga Plan
  doc.addPage();
  yPos = addHeader();
  
  addSectionTitle('Yoga & Meditation', '🧘');
  yPos += 5;

  if (plan.yoga_plan?.asanas) {
    plan.yoga_plan.asanas.slice(0, 6).forEach(asana => {
      checkNewPage(25);
      
      doc.setFontSize(11);
      doc.setFont('helvetica', 'bold');
      doc.setTextColor(26, 26, 46);
      doc.text(asana.name, margin, yPos);
      
      doc.setFont('helvetica', 'italic');
      doc.setFontSize(9);
      doc.setTextColor(150, 100, 200);
      doc.text(asana.sanskrit_name, margin + doc.getTextWidth(asana.name) + 5, yPos);
      yPos += 5;
      
      doc.setFont('helvetica', 'normal');
      doc.setFontSize(9);
      doc.setTextColor(100, 100, 100);
      doc.text(`${asana.duration_minutes} min | ${asana.difficulty}`, margin, yPos);
      yPos += 5;
      
      doc.setTextColor(34, 139, 34);
      doc.text('Benefits: ' + asana.benefits.slice(0, 3).join(', '), margin, yPos);
      yPos += 8;
    });
  }

  // Pranayama
  if (plan.yoga_plan?.pranayama && plan.yoga_plan.pranayama.length > 0) {
    yPos += 5;
    doc.setFontSize(11);
    doc.setTextColor(100, 100, 100);
    doc.text('Breathing Exercises (Pranayama):', margin, yPos);
    yPos += 6;
    plan.yoga_plan.pranayama.forEach(p => {
      addBulletPoint(`${p.name} - ${p.duration_minutes} min`);
    });
  }

  // Recommendations
  yPos += 10;
  addSectionTitle('Daily Recommendations', '💡');
  yPos += 3;
  
  if (plan.recommendations) {
    plan.recommendations.slice(0, 6).forEach(rec => {
      addBulletPoint(rec);
    });
  }

  addFooter(4, 4);

  // Save the PDF
  const fileName = `Infuse_Wellness_Plan_${new Date().toISOString().split('T')[0]}.pdf`;
  doc.save(fileName);
  
  return fileName;
};

/**
 * Export plan data as formatted text (for Word/manual copy)
 */
export const exportToText = (plan, patientInfo = {}) => {
  let content = '';
  
  content += '═'.repeat(60) + '\n';
  content += '                    INFUSE AI\n';
  content += '         ' + INFUSE_CONTACT.tagline + '\n';
  content += '═'.repeat(60) + '\n\n';
  
  content += '         AI LIFESTYLE CORRECTION PLAN\n\n';
  content += `Patient: ${patientInfo.name || 'N/A'}\n`;
  content += `Generated: ${new Date().toLocaleDateString('en-IN')}\n`;
  content += `Focus Areas: ${plan.focus_areas?.map(a => a.replace(/_/g, ' ')).join(', ') || 'General Wellness'}\n\n`;
  
  content += '─'.repeat(60) + '\n';
  content += '📊 QUICK STATS\n';
  content += '─'.repeat(60) + '\n';
  content += `Daily Calories: ${plan.diet_plan?.daily_calorie_target || 'N/A'} kcal\n`;
  content += `Hydration Target: ${plan.diet_plan?.hydration_target_liters || 'N/A'} L\n`;
  content += `Weekly Exercise: ${plan.workout_plan?.total_weekly_minutes || 'N/A'} min\n`;
  content += `Daily Yoga: ${plan.yoga_plan?.total_duration_minutes || 'N/A'} min\n\n`;
  
  content += '─'.repeat(60) + '\n';
  content += '🥗 DIET PLAN\n';
  content += '─'.repeat(60) + '\n';
  
  if (plan.diet_plan?.meals) {
    plan.diet_plan.meals.forEach(meal => {
      const mealName = meal.meal_type.replace(/_/g, ' ').toUpperCase();
      content += `\n${mealName} (${meal.time}) - ${meal.calories} kcal\n`;
      content += `  ${meal.foods.join(', ')}\n`;
    });
  }
  
  content += '\n─'.repeat(60) + '\n';
  content += '🏋️ WORKOUT PLAN\n';
  content += '─'.repeat(60) + '\n';
  
  if (plan.workout_plan?.exercises) {
    plan.workout_plan.exercises.forEach(ex => {
      content += `\n• ${ex.name}\n`;
      content += `  Duration: ${ex.duration_minutes} min | Intensity: ${ex.intensity}\n`;
      content += `  ${ex.instructions}\n`;
    });
  }
  
  content += '\n─'.repeat(60) + '\n';
  content += '🧘 YOGA & MEDITATION\n';
  content += '─'.repeat(60) + '\n';
  
  if (plan.yoga_plan?.asanas) {
    plan.yoga_plan.asanas.forEach(asana => {
      content += `\n• ${asana.name} (${asana.sanskrit_name})\n`;
      content += `  Duration: ${asana.duration_minutes} min | Level: ${asana.difficulty}\n`;
      content += `  Benefits: ${asana.benefits.join(', ')}\n`;
    });
  }
  
  content += '\n─'.repeat(60) + '\n';
  content += '💡 DAILY RECOMMENDATIONS\n';
  content += '─'.repeat(60) + '\n';
  
  if (plan.recommendations) {
    plan.recommendations.forEach(rec => {
      content += `• ${rec}\n`;
    });
  }
  
  content += '\n' + '═'.repeat(60) + '\n';
  content += `Generated by Infuse AI | ${INFUSE_CONTACT.website}\n`;
  content += `Contact: ${INFUSE_CONTACT.email}\n`;
  content += '═'.repeat(60) + '\n';
  
  return content;
};

/**
 * Download as Word document (using simple HTML to RTF conversion)
 */
export const exportToWord = (plan, patientInfo = {}) => {
  const textContent = exportToText(plan, patientInfo);
  
  // Create blob with RTF-compatible content
  const blob = new Blob([textContent], { type: 'application/msword' });
  const url = URL.createObjectURL(blob);
  
  const link = document.createElement('a');
  link.href = url;
  link.download = `Infuse_Wellness_Plan_${new Date().toISOString().split('T')[0]}.doc`;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
  
  return link.download;
};

export default { exportToPDF, exportToText, exportToWord };
